import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Zap, ArrowRight, CheckCircle2 } from 'lucide-react';

export default function Register() {
  const navigate = useNavigate();
  const [done, setDone] = useState(false);
  const [form, setForm] = useState({ full_name: '', email: '', password: '', password2: '', phone: '', company_name: '' });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.password !== form.password2) { toast.error('Le password non coincidono'); return; }
    if (!form.company_name.trim()) { toast.error("Inserisci il nome dell'azienda per cui lavori"); return; }
    if (form.password.length < 6) { toast.error('La password deve avere almeno 6 caratteri'); return; }
    setSubmitting(true);
    try {
      await api.register({
        full_name: form.full_name,
        email: form.email,
        password: form.password,
        phone: form.phone,
        company_name: form.company_name.trim(),
      });
      setDone(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Registrazione fallita');
    } finally { setSubmitting(false); }
  };

  if (done) {
    return (
      <div className="min-h-screen grid place-items-center bg-background p-6">
        <div className="w-full max-w-md text-center">
          <div className="mx-auto size-14 rounded-full bg-secondary grid place-items-center mb-4"><CheckCircle2 className="size-7 text-primary" /></div>
          <h2 className="font-heading text-2xl font-semibold mb-2">Registrazione ricevuta</h2>
          <p className="text-sm text-muted-foreground mb-6">Il tuo account è in attesa di approvazione da parte dell’amministratore di Scintilla Hub. Verificheremo l’azienda da te indicata e abiliteremo l’accesso al brand e ai listini personalizzati.</p>
          <Button asChild><Link to="/login">Torna al login</Link></Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      <div className="hidden lg:flex relative flex-col justify-between p-10 text-white overflow-hidden bg-[#0A0E0B]">
        <div className="absolute inset-0 opacity-90 bg-[radial-gradient(1200px_circle_at_20%_0%,rgba(159,255,92,0.16),transparent_55%),radial-gradient(900px_circle_at_80%_30%,rgba(20,184,166,0.10),transparent_50%)]" />
        <div className="relative z-10">
          <div className="flex items-center gap-2">
            <div className="size-9 rounded-xl grid place-items-center" style={{ background: '#9FFF5C', color: '#0A0E0B' }}><Zap className="size-5" /></div>
            <div>
              <div className="font-heading text-lg font-semibold">CRM Utility Boost</div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-white/60">By Scintilla Hub</div>
            </div>
          </div>
        </div>
        <div className="relative z-10 space-y-4 max-w-md">
          <h1 className="font-heading text-4xl font-semibold leading-tight">Diventa protagonista del <span style={{ color: '#9FFF5C' }}>cambiamento</span>.</h1>
          <p className="text-white/70 text-sm leading-relaxed">Unisciti agli agenti che ogni giorno portano l’energia pulita nelle case e nelle imprese italiane. Il pianeta ringrazia, le bollette si abbassano, il futuro si illumina.</p>
        </div>
        <div className="relative z-10 text-xs text-white/50">© {new Date().getFullYear()} Scintilla Hub</div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-10">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center gap-2 mb-6">
            <div className="size-9 rounded-xl bg-primary text-primary-foreground grid place-items-center"><Zap className="size-5" /></div>
            <div>
              <div className="font-heading text-lg font-semibold">CRM Utility Boost</div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">By Scintilla Hub</div>
            </div>
          </div>
          <h2 className="font-heading text-2xl font-semibold mb-2">Registrati come agente</h2>
          <p className="text-sm text-muted-foreground mb-6">Un futuro più verde e più pulito inizia da te. Indica l’azienda per cui lavori per iniziare.</p>

          <Card>
            <CardContent className="p-5">
              <form onSubmit={handleSubmit} className="space-y-3">
                <div className="space-y-1.5"><Label htmlFor="name">Nome e Cognome *</Label><Input id="name" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} required data-testid="register-name-input" /></div>
                <div className="space-y-1.5"><Label htmlFor="email">Email *</Label><Input id="email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required data-testid="register-email-input" /></div>
                <div className="space-y-1.5"><Label htmlFor="phone">Telefono</Label><Input id="phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
                <div className="space-y-1.5">
                  <Label htmlFor="company">Azienda per cui lavori *</Label>
                  <Input id="company" value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} required placeholder="Es. Energia Sole S.r.l." data-testid="register-company-input" />
                  <p className="text-xs text-muted-foreground">Scrivi il nome esatto dell’azienda partner per cui collabori. Lo staff Scintilla Hub verificherà la richiesta prima di abilitare l’accesso.</p>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5"><Label htmlFor="password">Password *</Label><Input id="password" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required data-testid="register-password-input" /></div>
                  <div className="space-y-1.5"><Label htmlFor="password2">Conferma *</Label><Input id="password2" type="password" value={form.password2} onChange={(e) => setForm({ ...form, password2: e.target.value })} required data-testid="register-password2-input" /></div>
                </div>
                <Button type="submit" className="w-full" disabled={submitting} data-testid="register-submit-button">{submitting ? 'Invio...' : 'Registrati'} <ArrowRight className="size-4 ml-1" /></Button>
              </form>
            </CardContent>
          </Card>
          <div className="text-sm text-muted-foreground mt-6 text-center">Hai già un account? <Link to="/login" className="text-foreground font-medium hover:underline">Accedi</Link></div>
        </div>
      </div>
    </div>
  );
}
